import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from './components/PageHeader';
import { apiUrl } from './utils/api';

const API_BASE = apiUrl('/api/marketing/contacts');

const ContactManager = () => {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, byCategory: {}, byStatus: {}, bySource: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchContacts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(search && { search }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      const token = localStorage.getItem('doorbase_token');
      const res = await fetch(`${API_BASE}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setContacts(data.contacts || []);
      setPagination(data.pagination || {});
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter, sourceFilter, sortBy, sortDir]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('doorbase_token');
      const res = await fetch(`${API_BASE}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchContacts(1);
    fetchStats();
  }, [fetchContacts, fetchStats]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContacts(1);
  };

  const handlePageChange = (newPage) => {
    fetchContacts(newPage);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)));
    }
  };

  const handleBulkCategory = async (category) => {
    if (selectedIds.size === 0) return;
    const token = localStorage.getItem('doorbase_token');
    try {
      const res = await fetch(`${API_BASE}/bulk-category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds), category })
      });
      if (res.ok) {
        setSelectedIds(new Set());
        fetchContacts(pagination.page);
        fetchStats();
      }
    } catch (err) {
      console.error('Error updating categories:', err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} contacts?`)) return;
    const token = localStorage.getItem('doorbase_token');
    try {
      // Delete individually as workaround for bulk-delete route bug
      let deleted = 0;
      for (const id of Array.from(selectedIds)) {
        const res = await fetch(`${API_BASE}/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) deleted++;
      }
      setSelectedIds(new Set());
      fetchContacts(1);
      fetchStats();
    } catch (err) {
      console.error('Error deleting contacts:', err);
    }
  };

  const handleExport = (selectedOnly = false) => {
    const params = new URLSearchParams();
    if (selectedOnly && selectedIds.size > 0) {
      params.set('ids', Array.from(selectedIds).join(','));
    } else {
      if (categoryFilter) params.set('category', categoryFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (sourceFilter) params.set('source', sourceFilter);
    }
    const token = localStorage.getItem('doorbase_token');
    window.open(`${API_BASE}/export?${params}&token=${token}`, '_blank');
  };

  const handleSyncCustomers = async () => {
    setSyncing(true);
    const token = localStorage.getItem('doorbase_token');
    try {
      const res = await fetch(`${API_BASE}/sync-customers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      alert(`Sync complete: ${data.added} added, ${data.updated} updated from ${data.total} customers`);
      fetchContacts(1);
      fetchStats();
    } catch (err) {
      console.error('Error syncing customers:', err);
      alert('Error syncing customers');
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async (file) => {
    setImporting(true);
    setImportResult(null);
    const token = localStorage.getItem('doorbase_token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_BASE}/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setImportResult(data);
      fetchContacts(1);
      fetchStats();
    } catch (err) {
      console.error('Error importing:', err);
      setImportResult({ error: 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const handleCategoryChange = async (id, category) => {
    const token = localStorage.getItem('doorbase_token');
    try {
      const res = await fetch(`${API_BASE}/${id}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category })
      });
      if (res.ok) {
        fetchContacts(pagination.page);
        fetchStats();
      }
    } catch (err) {
      console.error('Error updating category:', err);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    const token = localStorage.getItem('doorbase_token');
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchContacts(pagination.page);
        fetchStats();
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      domestic: 'bg-blue-100 text-blue-800',
      commercial: 'bg-green-100 text-green-800',
    };
    return styles[category] || 'bg-gray-100 text-gray-800';
  };

  const getSourceBadge = (source) => {
    const styles = {
      manual: 'bg-purple-100 text-purple-800',
      import: 'bg-yellow-100 text-yellow-800',
      doorbase: 'bg-indigo-100 text-indigo-800',
    };
    return styles[source] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      unsubscribed: 'bg-red-100 text-red-800',
      bounced: 'bg-orange-100 text-orange-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Contact Manager"
        subtitle="Marketing contacts & audience management"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-blue-600 uppercase tracking-wide">Domestic</div>
            <div className="text-2xl font-bold text-blue-600">{stats.byCategory?.domestic || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-green-600 uppercase tracking-wide">Commercial</div>
            <div className="text-2xl font-bold text-green-600">{stats.byCategory?.commercial || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Active</div>
            <div className="text-2xl font-bold text-gray-900">{stats.byStatus?.active || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Uncategorised</div>
            <div className="text-2xl font-bold text-gray-500">{stats.byCategory?.uncategorised || 0}</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          {/* Row 1: Search + Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-[240px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, company..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 whitespace-nowrap">
                Search
              </button>
            </form>

            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              <option value="domestic">Domestic</option>
              <option value="commercial">Commercial</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Sources</option>
              <option value="manual">Manual</option>
              <option value="import">Import</option>
              <option value="doorbase">DoorBase</option>
            </select>
          </div>

          {/* Row 2: Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700"
            >
              + Add Contact
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700"
            >
              📥 Import CSV
            </button>
            <button
              onClick={() => {
                const headers = 'Email,First Name,Last Name,Company Name,Phone,Category,Tags,Notes';
                const example = 'john@example.com,John,Smith,ACME Ltd,01234 567890,commercial,"school;NHS",Priority customer';
                const csv = headers + '\n' + example;
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'import-template.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
            >
              📋 Download Template
            </button>
            <button
              onClick={() => handleExport(false)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700"
            >
              📤 Export All
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={() => handleExport(true)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-600"
              >
                📤 Export Selected ({selectedIds.size})
              </button>
            )}
            <button
              onClick={handleSyncCustomers}
              disabled={syncing}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {syncing ? 'Syncing...' : '🔄 Sync Customers'}
            </button>
          </div>

          {/* Bulk Actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
              <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
              <button onClick={() => handleBulkCategory('domestic')} className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm hover:bg-blue-200">
                → Domestic
              </button>
              <button onClick={() => handleBulkCategory('commercial')} className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm hover:bg-green-200">
                → Commercial
              </button>
              <button onClick={handleBulkDelete} className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200">
                🗑 Delete
              </button>
            </div>
          )}
        </div>

        {/* Contact Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg mb-2">No contacts yet</p>
              <p className="text-sm">Import a CSV, sync from DoorBase customers, or add contacts manually.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === contacts.length && contacts.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => { setSortBy('first_name'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>
                      Name {sortBy === 'first_name' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => { setSortBy('email'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}>
                      Email {sortBy === 'email' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contacts.map(contact => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contact.id)}
                          onChange={() => toggleSelect(contact.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {contact.first_name} {contact.last_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{contact.email}</td>
                      <td className="px-4 py-3 text-gray-600">{contact.company_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{contact.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <select
                          value={contact.category || ''}
                          onChange={(e) => handleCategoryChange(contact.id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${getCategoryBadge(contact.category)}`}
                        >
                          <option value="domestic">Domestic</option>
                          <option value="commercial">Commercial</option>
                          <option value="">Uncategorised</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getSourceBadge(contact.source)}`}>
                          {contact.source || 'manual'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusBadge(contact.status)}`}>
                          {contact.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingContact(contact)}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  ← Prev
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add Contact Modal */}
      {showAddModal && (
        <AddEditModal
          onSave={async (contact) => {
            const token = localStorage.getItem('doorbase_token');
            const res = await fetch(API_BASE, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(contact)
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              alert(err.error || `Failed to add contact (${res.status})`);
              return;
            }
            setShowAddModal(false);
            fetchContacts(1);
            fetchStats();
          }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <AddEditModal
          contact={editingContact}
          onSave={async (contact) => {
            const token = localStorage.getItem('doorbase_token');
            const res = await fetch(`${API_BASE}/${editingContact.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(contact)
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              alert(err.error || `Failed to update contact (${res.status})`);
              return;
            }
            setEditingContact(null);
            fetchContacts(pagination.page);
          }}
          onClose={() => setEditingContact(null)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onImport={handleImport}
          importing={importing}
          importResult={importResult}
          onClose={() => { setShowImportModal(false); setImportResult(null); }}
        />
      )}
    </div>
  );
};

// Add/Edit Contact Modal
const AddEditModal = ({ contact, onSave, onClose }) => {
  const [form, setForm] = useState({
    email: contact?.email || '',
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    company_name: contact?.company_name || '',
    phone: contact?.phone || '',
    category: contact?.category || 'commercial',
    tags: contact?.tags || [],
    notes: contact?.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{contact ? 'Edit Contact' : 'Add Contact'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input type="text" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="commercial">Commercial</option>
              <option value="domestic">Domestic</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (semicolon separated)</label>
            <input type="text" value={form.tags?.join('; ') || ''} onChange={e => setForm({...form, tags: e.target.value.split(';').map(t => t.trim()).filter(Boolean)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. school; NHS; priority" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={3} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              {contact ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Import Modal
const ImportModal = ({ onImport, importing, importResult, onClose }) => {
  const [file, setFile] = useState(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) onImport(file);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Import Contacts from CSV</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
          >
            <input
              type="file"
              accept=".csv"
              onChange={e => setFile(e.target.files[0])}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              {file ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <p className="text-4xl mb-2">📄</p>
                  <p className="text-sm text-gray-600">Drop a CSV file here or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">Columns: Email, First Name, Last Name, Company Name, Phone, Category, Tags</p>
                </div>
              )}
            </label>
          </div>

          {importResult && (
            <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
              {importResult.error ? (
                <p className="text-sm">{importResult.error}</p>
              ) : (
                <div className="text-sm">
                  <p className="font-medium">Import complete</p>
                  <p>{importResult.inserted} added, {importResult.updated} updated, {importResult.skipped} skipped ({importResult.total} total)</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button
              type="submit"
              disabled={!file || importing}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {importing ? 'Importing...' : 'Import'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactManager;
